import html2canvas from 'html2canvas';
import JsPDF from 'jspdf';
import useFlags from 'launchdarkly-react-client-sdk/lib/useFlags';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAdminDashboardFiltersConfig from './AdminDashboardFilters.config';
import { FilterKey, SelectedFiltersState } from './AdminDashboardFilters.types';
import { AllValidTherapeuticArea } from 'basics/enums/biogen.enums';
import { useCreateOrUpdateMayaUserDataMutation } from 'basics/graphql/mutations/createOrUpdateMayaUserData';
import useExportAdminDashboardDataQuery from 'basics/graphql/queries/exportAdminDashboardData';
import { SelectOption } from 'basics/options/options.types';
import { CountryDataType } from 'basics/types/countryData.types';
import { ProductType } from 'basics/types/products.types';
import { CurrentUser } from 'basics/types/user.types';
import { errorToast, succesToast } from 'basics/utils/toast';
import { formatAdminDashboardFilter, formatUserDashboardFilter } from 'components/AdminDashboardFilters/AdminDashboardFilters.transformer';
import { CountriesContext } from 'contexts/countries';
import { useGTM } from 'contexts/gtm';
import { ProductsContext } from 'contexts/products';
import { CurrentUserStateContext, useCurrentUserState } from 'contexts/user';
import { DashboardFilter } from 'generated/maya.types';
import { ButtonType } from 'yoda-ui/components/Button/Button.types';
import { useYodaCreateForm } from 'yoda-ui/yodaForm';

const useAdminDashboardFilters = () => {
  const flags = useFlags();
  const [selectedTA, setSelectedTA] = useState<string[] | null>(null);
  const [filters, setFilters] = useState<DashboardFilter | undefined>();
  const { getValues, providerFields, setValue, useWatchField } = useYodaCreateForm();
  const { t } = useTranslation();
  const { user } = useCurrentUserState();
  const { setCurrentUserData } = useContext(CurrentUserStateContext);
  const { allProducts, productList } = useContext(ProductsContext);
  const [dashboardProductList, setDashboardProductList] = useState<ProductType[]>(allProducts);
  const { adminDashboardFiltersConfig } = useAdminDashboardFiltersConfig(selectedTA, dashboardProductList, user);
  const [createOrUpdateMayaUserData] = useCreateOrUpdateMayaUserDataMutation();
  const therapeuticAreaKey = useWatchField(adminDashboardFiltersConfig.therapeuticArea.name)?.value;
  const [triggerDownload, setTriggerDownload] = useState<boolean>(false);
  const { data, error, exportAdminDashboardData, loading: exportLoading } = useExportAdminDashboardDataQuery();

  const chartsRef = useRef<HTMLDivElement>(null);
  const [triggerExportCharts, setTriggerExportCharts] = useState<boolean>(false);
  const dataLayer = useGTM();
  const countryDropdownValues = useWatchField(adminDashboardFiltersConfig.countries.name)?.value;
  const { countries } = useContext(CountriesContext);
  const hasInitializedFilters = useRef(false);
  const autoFillCountriesLocked = useRef(false);

  useEffect(() => {
    setSelectedTA(therapeuticAreaKey);
  }, [therapeuticAreaKey]);

  const getProductsForCountries = useCallback((countryCodes: (string | number)[]): ProductType[] => {
    if (!countryCodes || countryCodes.length === 0) {
      return [];
    }

    const productIdsInSelectedCountries = new Set(
      countries
        .filter((country) => countryCodes.includes(country.code))
        .flatMap((country: CountryDataType) => country.products),
    );

    return productList.filter((product) => productIdsInSelectedCountries.has(product.productExternalID));
  }, [countries, productList]);

  useEffect(() => {
    if (!flags.mayaAkma519EnableProductFilteringByCountry) {
      setDashboardProductList(allProducts);
      return;
    }

    let countryCodesToFilter: Array<string | number> = [];

    const hasSelectedCountriesInUI = countryDropdownValues?.length > 0;
    const hasSavedCountryFilters = user?.adminDashboardFilters?.countries ? user?.adminDashboardFilters?.countries?.length > 0 : false;
    const countryOptionsAreReady = adminDashboardFiltersConfig.countries.options.length > 0;

    if (hasSelectedCountriesInUI) {
      countryCodesToFilter = countryDropdownValues;
    } else if (!hasSavedCountryFilters && countryOptionsAreReady) {
      countryCodesToFilter = adminDashboardFiltersConfig.countries.options
        .map((country) => country.id);
      autoFillCountriesLocked.current = true;
    }

    const filteredProducts = getProductsForCountries(countryCodesToFilter);
    setDashboardProductList(filteredProducts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    countryDropdownValues,
    productList,
    countries,
    flags.mayaAkma519EnableProductFilteringByCountry,
    adminDashboardFiltersConfig.countries.options,
    user?.adminDashboardFilters?.countries,
    getProductsForCountries,
    allProducts,
  ]);

  useEffect(() => {
    if (dashboardProductList.length > 0 && !hasInitializedFilters.current) {
      if (user?.adminDashboardFilters) {
        let formattedFilters = formatAdminDashboardFilter(user.adminDashboardFilters, dashboardProductList);

        if (formattedFilters?.therapeuticArea?.includes(AllValidTherapeuticArea.BBU)) {
          formattedFilters = {
            ...formattedFilters,
            therapeuticArea: [...formattedFilters.therapeuticArea, AllValidTherapeuticArea.BIOSIMS],
          };
        }

        setFilters(formattedFilters);
        hasInitializedFilters.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardProductList.length, user]);

  useEffect(() => {
    if (error) {
      setTriggerDownload(false);
    }

    if (!exportLoading && data && triggerDownload) {
      const url = URL.createObjectURL(new Blob(
        [data as Buffer],
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      ));
      const event = new MouseEvent('click');
      const el = document.createElement('a');
      el.setAttribute('href', url);
      el.setAttribute('download', `export_maya_${new Date().toISOString().split('T')[0]}`);
      el.dispatchEvent(event);
      URL.revokeObjectURL(url);

      setTriggerDownload(false);
      succesToast(t('maya_admin_dashboard_data_exported'));
    } else if (triggerDownload) {
      errorToast(t('maya_admin_dashboard_no_data_to_exported'));
    }
  }, [exportLoading, data, error, triggerDownload, t]);

  const handleChangeTA = useCallback((options: SelectOption[]) => {
    if (options.length === 0) {
      setValue(adminDashboardFiltersConfig.product.name, []);
    }
  }, [setValue, adminDashboardFiltersConfig]);

  const handleSearchValueOfUserFilters = () => {
    let formattedFilters = formatAdminDashboardFilter(getValues(), dashboardProductList);

    if (formattedFilters?.therapeuticArea?.includes(AllValidTherapeuticArea.BBU)) {
      formattedFilters = {
        ...formattedFilters,
        therapeuticArea: [...formattedFilters.therapeuticArea, AllValidTherapeuticArea.BIOSIMS],
      };
    }

    setFilters(formattedFilters);

    const userFilters = formatUserDashboardFilter(getValues());
    createOrUpdateMayaUserData({ adminDashboardFilters: userFilters });
    setCurrentUserData((prevState) => {
      return {
        ...prevState,
        currentUser: {
          ...prevState.currentUser,
          adminDashboardFilters: userFilters,
        } as CurrentUser,
      };
    });
  };

  const handleExportDataOfUserFilters = async () => {
    let formattedFilters = formatAdminDashboardFilter(getValues(), dashboardProductList);

    if (formattedFilters?.therapeuticArea?.includes(AllValidTherapeuticArea.BBU)) {
      formattedFilters = {
        ...formattedFilters,
        therapeuticArea: [...formattedFilters.therapeuticArea, AllValidTherapeuticArea.BIOSIMS],
      };
    }

    await exportAdminDashboardData({
      variables: {
        filters: formattedFilters,
      },
    });

    dataLayer({
      event: 'export_data_dashboard',
    });

    setTriggerDownload(true);
  };

  const handleExportChartsPDFOfUserFilters = async () => {
    if (chartsRef.current) {
      setTriggerExportCharts(true);

      await new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 0);
      });
      window.scrollTo(0, 0);

      const canvas = await html2canvas(chartsRef.current, {
        useCORS: true,
        scale: 2,
      });

      const imgData = canvas.toDataURL('image/png');

      const pdf = new JsPDF({
        unit: 'px',
        format: [canvas.width + 150, canvas.height + 150],
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const x = (pageWidth - imgWidth) / 2;
      const y = (pageHeight - imgHeight) / 2;

      pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);
      pdf.save(`export_maya_dashboard_charts_${new Date().toISOString().split('T')[0]}.pdf`);
    }

    dataLayer({
      event: 'export_charts_dashboard',
    });
    succesToast(t('maya_admin_dashboard_charts_exported'));
    setTriggerExportCharts(false);
  };

  const resetDashboardFilter = () => {
    const dashboardFields = Object.keys(adminDashboardFiltersConfig);

    dashboardFields.forEach((field) => {
      const fieldName = adminDashboardFiltersConfig[field as keyof typeof adminDashboardFiltersConfig].name;

      const isArrayField = [
        adminDashboardFiltersConfig.countries.name,
        adminDashboardFiltersConfig.therapeuticArea.name,
        adminDashboardFiltersConfig.product.name,
        adminDashboardFiltersConfig.cepFunction.name,
        adminDashboardFiltersConfig.cepStatus.name,
      ].includes(fieldName);

      setValue(
        fieldName,
        isArrayField ? [] : '',
      );
    });
  };

  const exportDataButtonConfig = {
    buttonProps: {
      onClick: handleExportDataOfUserFilters,
      buttonType: ButtonType.outlined,
      disabled: !dashboardProductList.length || exportLoading,
    },
    label: t('maya_admin_dashboard_export_data_label'),
  };

  const exportChartsButtonConfig = {
    buttonProps: {
      onClick: handleExportChartsPDFOfUserFilters,
      buttonType: ButtonType.outlined,
      disabled: !dashboardProductList.length || triggerExportCharts,
    },
    label: t('maya_admin_dashboard_export_charts_pdf_label'),
  };

  const getRestoredOptions = useCallback((
    values: string[] | undefined,
    allOptions: SelectOption[] = [],
    matchBy: 'id' | 'label' = 'id',
  ): SelectOption[] => {
    if (!values || !Array.isArray(values)) return [];

    if (allOptions.length === 0) return [];

    return allOptions.filter((opt) => values.includes(opt[matchBy] as string));
  }, []);

  const generateStateFromUser = useCallback((userData: typeof user) => {
    const adminDashboardFilters = userData?.adminDashboardFilters;

    return {
      countries: getRestoredOptions(
        adminDashboardFilters?.countries as string[],
        adminDashboardFiltersConfig.countries.options,
        'id',
      ),
      therapeuticArea: getRestoredOptions(
        adminDashboardFilters?.therapeuticArea as string[],
        adminDashboardFiltersConfig.therapeuticArea.options,
        'id',
      ),
      product: getRestoredOptions(
        adminDashboardFilters?.product as string[],
        adminDashboardFiltersConfig.product.options,
        'id',
      ),
      cepFunction: getRestoredOptions(
        adminDashboardFilters?.cepFunction as string[],
        adminDashboardFiltersConfig.cepFunction.options,
        'id',
      ),
      cepStatus: getRestoredOptions(
        adminDashboardFilters?.cepStatus as string[],
        adminDashboardFiltersConfig.cepStatus.options,
        'id',
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminDashboardFiltersConfig, dashboardProductList.length]);

  const [selectedFilters, setSelectedFilters] = useState<SelectedFiltersState>(() => generateStateFromUser(user));

  useEffect(() => {
    if (user?.adminDashboardFilters && adminDashboardFiltersConfig.product.options.length > 0) {
      setSelectedFilters(generateStateFromUser(user));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminDashboardFiltersConfig.product.options.length, user]);

  useEffect(() => {
    if (!user?.adminDashboardFilters) return;

    const { product: savedProductIds, therapeuticArea: savedTA } = user.adminDashboardFilters;
    const productOptions = adminDashboardFiltersConfig.product.options;
    const taOptions = adminDashboardFiltersConfig.therapeuticArea.options;

    if (savedProductIds?.length && productOptions.length > 0 && selectedFilters.product.length === 0) {
      const restoredProducts = getRestoredOptions(savedProductIds, productOptions, 'id');

      if (restoredProducts.length > 0) {
        setSelectedFilters((prev) => ({ ...prev, product: restoredProducts }));
      }
    }

    if (savedTA?.length && taOptions.length > 0 && selectedFilters.therapeuticArea.length === 0) {
      const restoredTA = getRestoredOptions(savedTA, taOptions, 'id');

      if (restoredTA.length > 0) {
        setSelectedFilters((prev) => ({ ...prev, therapeuticArea: restoredTA }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    adminDashboardFiltersConfig.product.options.length,
    adminDashboardFiltersConfig.therapeuticArea.options.length,
    user,
    getRestoredOptions,
    selectedFilters.product.length,
    selectedFilters.therapeuticArea.length,
  ]);

  const handleFilterChange = (key: FilterKey, options: SelectOption[]) => {
    const fieldName = adminDashboardFiltersConfig[key].name;
    const currentFormValues = getValues();
    const values = options.map((o) => o.id as string);

    setValue(fieldName, values);

    const newSelectedFilters = { ...selectedFilters, [key]: options };
    setSelectedFilters(newSelectedFilters);

    if (key === 'therapeuticArea') {
      handleChangeTA(options);
    }

    const filterData = {
      countries: key === 'countries' ? options.map((opt) => opt.id) : newSelectedFilters.countries.map((opt) => opt.id),
      therapeuticArea: key === 'therapeuticArea' ? options.map((opt) => opt.id) : newSelectedFilters.therapeuticArea.map((opt) => opt.id),
      product: key === 'product' ? options.map((opt) => opt.id) : newSelectedFilters.product.map((opt) => opt.id),
      cepFunction: key === 'cepFunction' ? options.map((opt) => opt.id) : newSelectedFilters.cepFunction.map((opt) => opt.id),
      cepStatus: key === 'cepStatus' ? options.map((opt) => opt.id) : newSelectedFilters.cepStatus.map((opt) => opt.id),
      date: currentFormValues.date,
    };

    let formattedFilters = formatAdminDashboardFilter(filterData, dashboardProductList);

    if (formattedFilters?.therapeuticArea?.includes(AllValidTherapeuticArea.BBU)) {
      formattedFilters = {
        ...formattedFilters,
        therapeuticArea: [...formattedFilters.therapeuticArea, AllValidTherapeuticArea.BIOSIMS],
      };
    }

    setFilters(formattedFilters);

    const userFilters = formatUserDashboardFilter(filterData);
    createOrUpdateMayaUserData({ adminDashboardFilters: userFilters });
    setCurrentUserData((prevState) => ({
      ...prevState,
      currentUser: {
        ...prevState.currentUser,
        adminDashboardFilters: userFilters,
      } as CurrentUser,
    }));
  };

  const handleRemoveChip = (key: FilterKey, idToRemove: string | number) => {
    const newOptions = selectedFilters[key].filter((o) => o.id !== idToRemove);
    handleFilterChange(key, newOptions);
  };

  const handleReset = () => {
    setSelectedFilters({
      countries: [],
      therapeuticArea: [],
      product: [],
      cepFunction: [],
      cepStatus: [],
    });

    resetDashboardFilter();

    const emptyFilters = formatAdminDashboardFilter({
      countries: [],
      therapeuticArea: [],
      product: [],
      cepFunction: [],
      cepStatus: [],
      date: [],
    }, dashboardProductList);

    setFilters(emptyFilters);
    createOrUpdateMayaUserData({ adminDashboardFilters: {} });
    setCurrentUserData((prevState) => {
      return {
        ...prevState,
        currentUser: {
          ...prevState.currentUser,
          adminDashboardFilters: {},
        } as CurrentUser,
      };
    });
  };

  const hasSelectedFilters = Object.values(selectedFilters).some((opts) => opts.length > 0);

  return {
    providerFields,
    adminDashboardFiltersConfig,
    exportDataButtonConfig,
    exportChartsButtonConfig,
    filters,
    products: dashboardProductList,
    chartsRef,
    triggerExportCharts,
    resetDashboardFilter,
    handleSearchValueOfUserFilters,
    selectedFilters,
    handleFilterChange,
    handleRemoveChip,
    hasSelectedFilters,
    handleReset,
  };
};

export default useAdminDashboardFilters;
